import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { createPortal } from "react-dom";
import Spinner from './spinner';
import '../styles/footer.scss';
import modalStyles from '../styles/modal.module.scss';
import formStyles from '../styles/forms.module.scss';
import classNames from 'classnames';
import { useStaticQuery } from 'gatsby';
import chevrons from '../images/chevrons-form.svg';
import { isEmail, isPostalCode } from 'validator';
import axios from 'axios';

export const Checkbox = ({ children, id, isSelected, toggleSelected }) => {
  return (
    <div className={formStyles.checkbox}>
      <input
        type="checkbox"
        id={id}
        name={id}
        checked={isSelected}
        onChange={toggleSelected}
        className={formStyles.checkboxInput}
      />
      <label className={formStyles.checkboxLabel} htmlFor={id}>
        <div className={formStyles.checkboxBox} />
        <div className={formStyles.checkboxLabelTitle}>{children}</div>
      </label>
    </div>
  )
}

const FormChevrons = () => (
  <svg className={formStyles.chevrons} viewBox="0 0 700 157.501">
    <g data-name="Group 2" transform="translate(-220.982 -730.901)">
      <path
        data-name="Rectangle 11"
        transform="translate(220.982 730.901)"
        className="fill-fireworks-gold"
        d="M0 0H350V157.501H0z"
      />
      <path
        data-name="Path 12"
        d="M0 0h350v157.5H0z"
        transform="translate(570.982 730.901)"
        className="fill-artillery-red"
      />
      <path
        data-name="Path 10"
        d="M1348.192 627.194l87.5-87.5 87.5 87.5 87.5-87.5v-70l-87.5 87.5-87.5-87.5-87.5 87.5-87.5-87.5v70z"
        transform="translate(-1039.709 261.207)"
        className="fill-navy-blue"
      />
      <path
        data-name="Path 11"
        d="M1348.192 627.194l87.5-87.5 87.5 87.5 87.5-87.5v-70l-87.5 87.5-87.5-87.5-87.5 87.5-87.5-87.5v70z"
        transform="translate(-689.709 261.207)"
        className="fill-drumskin-white"
      />
    </g>
  </svg>
)

const SignUpForm = ({ handleClose, emailFromPage }) => {
  const data = useStaticQuery(graphql`
    query SignUpFormQuery {      
      footer: sanityFooter {
        formTitle
        formIntroText
        formSubmitButtonText
        formQuestion
        formThanksTitle
        formThanksMessage
        _rawFormTermsDeclaration
      }
    }
  `);

  const [showSpinner, setShowSpinner] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const [theatre, setTheatre] = useState(false);
  const [art, setArt] = useState(false);
  const [community, setCommunity] = useState(false);
  const [contemporary, setContemporary] = useState(false);
  const [classical, setClassical] = useState(false);
  const [comedy, setComedy] = useState(false);
  const [literature, setLiterature] = useState(false);
  const [family, setFamily] = useState(false);

  const toggleTheatre = () => {
    setTheatre(!theatre);
  }

  const toggleArt = () => {
    setArt(!art);
  }

  const toggleCommunity = () => {
    setCommunity(!community);
  }

  const toggleContemporary = () => {
    setContemporary(!contemporary);
  }

  const toggleClassical = () => {
    setClassical(!classical);
  }

  const toggleComedy = () => {
    setComedy(!comedy);
  }

  const toggleLiterature = () => {
    setLiterature(!literature);
  }

  const toggleFamily = () => {
    setFamily(!family);
  }

  const [email, setEmail] = useState(emailFromPage || '');
  const [emailValidates, setEmailValidates] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [firstNameValidates, setFirstNameValidates] = useState(null);
  const [lastName, setLastName] = useState('');
  const [lastNameValidates, setLastNameValidates] = useState(null);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [town, setTown] = useState('');
  const [townValidates, setTownValidates] = useState(null);
  const [postcode, setPostcode] = useState('');
  const [postcodeValidates, setPostcodeValidates] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formIsValid = validateForm();
    if (formIsValid) {
      sendData();
      setShowSpinner(true);
    }
  }

  const validateForm = () => {
    const validEmail = isEmail(email);
    const validFirstName = firstName.length > 2;
    const validLastName = lastName.length > 2;
    const validAddress1 = address1 !== null && address1.length > 2;
    const validTown = town !== null && town.length > 2;
    const validPostcode = postcode !== null && isPostalCode(postcode, 'GB');

    // set state vars for in-page errors
    setEmailValidates(validEmail);
    setFirstNameValidates(validFirstName);
    setLastNameValidates(validLastName);

    if (validAddress1) {
      setTownValidates(validTown);
      setPostcodeValidates(validPostcode);
    }

    // return validation
    const validAddress = validAddress1 ? validTown && validPostcode : true;
    return validEmail && validFirstName && validLastName && validAddress;
  }

  const sendData = async () => {
    console.log('form validated. sending data...');

    const customerInfo = {
      email: email,
      firstName: firstName,
      lastName: lastName,
      AgreedStatements: ["201AGBHDRLQHNHPHKKMPKLGPMDRDTDMVL"],
    }

    if (postcodeValidates) {
      customerInfo.Addresses = [
        {
          name: `${firstName} ${lastName}`,
          isDelivery: true,
          isBilling: true,
          administrativeDivision: '',
          line1: address1,
          line2: address2 || '',
          town: town,
          postcode: postcode,
          country: 'GB'
        }
      ]
    }

    customerInfo.Tags = [];
    if (theatre) customerInfo.Tags.push('201AGBHDRLQHNHPHKKMPKLGPMDRDTDMVL');
    if (art) customerInfo.Tags.push('401ANPJQJQPQMRSBDNMVNLSPGTRBVQVRH');
    if (community) customerInfo.Tags.push('402AJKCLLRDNMBKMHTCGLBDHJTBPJBDNV');
    if (contemporary) customerInfo.Tags.push('601APNNMRMBJQQPBSCNQGQHGKKCRPSSGN');
    if (classical) customerInfo.Tags.push('801ARDQDDMGGJKKRTNTJBMCCMMBCPQKCR');
    if (comedy) customerInfo.Tags.push('1001ADGKSHLJDTDBJQTBMGLLJRLBJCMNN');
    if (literature) customerInfo.Tags.push('1201AQKTLDJVPVNVGVNTLHSSDPLSGRGBT');
    if (family) customerInfo.Tags.push('1401AHPQBSMRKMCGHQKGHVQVKCQNVNBKN');

    // console.log(JSON.stringify(customerInfo));

    try {
      const apiUrl = 'https://system.spektrix.com/woolwichworks/api/v3/customer';
      const res = await axios.post(apiUrl, JSON.stringify(customerInfo), {
        headers: {
          'Content-Type': 'application/json',
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setShowSpinner(false);
      setShowThanks(true);
    }
  }

  return (
    <>
      {
        createPortal(
          <div className={modalStyles.modalContainer}>
            <div className={modalStyles.modalClickscreen} onClick={() => showSpinner ? null : handleClose()} />
            {
              showSpinner &&
              <Spinner />
            }
            {
              !showSpinner &&
              <div className={modalStyles.modalBox}>
                <div className={modalStyles.modalScrollBox}>
                  <button className={modalStyles.closeButton} onClick={handleClose}>&times;</button>
                  <div className={modalStyles.modalFormContainer}>
                    {
                      showThanks &&
                      <>
                        <div className='keep-in-touch__title'>{data.footer.formThanksTitle}</div>
                        {
                          data.footer.formThanksMessage &&
                          <div className='keep-in-touch__intro'>{data.footer.formThanksMessage}</div>
                        }
                        <button onClick={handleClose} className={classNames('button', formStyles.signUpButton)}>
                          {data.footer.formThanksButtonText || 'OK'}
                        </button>
                      </>
                    }
                    {
                      !showThanks &&
                      <form noValidate onSubmit={handleSubmit}>
                        <div className='keep-in-touch__title'>{data.footer.formTitle}</div>
                        <div className='keep-in-touch__intro'>{data.footer.formIntroText}</div>

                        <input
                          className={formStyles.input}
                          type="email"
                          name="email"
                          required
                          placeholder='Email'
                          value={email}
                          onChange={(e) => setEmail(e.currentTarget.value)}
                          onBlur={validateForm}
                          maxLength={60}
                        />
                        {
                          emailValidates === false &&
                          <div className={formStyles.error}>Please check your email address</div>
                        }

                        <input
                          className={formStyles.input}
                          type="text"
                          name="firstName"
                          required
                          placeholder='First name'
                          value={firstName}
                          onChange={(e) => setFirstName(e.currentTarget.value)}
                          onBlur={validateForm}
                          maxLength={40}
                        />
                        {
                          firstNameValidates === false &&
                          <div className={formStyles.error}>Please check your first name</div>
                        }

                        <input
                          className={formStyles.input}
                          type="text"
                          name="lastName"
                          required
                          placeholder='Last name'
                          value={lastName}
                          onChange={(e) => setLastName(e.currentTarget.value)}
                          onBlur={validateForm}
                          maxLength={40}
                        />
                        {
                          lastNameValidates === false &&
                          <div className={formStyles.error}>Please check your last name</div>
                        }

                        <div className={formStyles.optional}>Optional:</div>

                        <input
                          className={formStyles.input}
                          type="text"
                          name="addressLine1"
                          placeholder='Address line 1'
                          value={address1}
                          onChange={(e) => setAddress1(e.currentTarget.value)}
                          onBlur={validateForm}
                          maxLength={40}
                        />

                        <input
                          className={formStyles.input}
                          type="text"
                          name="addressLine2"
                          placeholder='Address line 2'
                          value={address2}
                          onChange={(e) => setAddress2(e.currentTarget.value)}
                          onBlur={validateForm}
                          maxLength={40}
                        />

                        <div className={classNames(formStyles.doubleBlock, formStyles.doubleBlockTwoFields)}>
                          <input
                            className={formStyles.input}
                            type="text"
                            name="town"
                            placeholder='Town'
                            value={town}
                            onChange={(e) => setTown(e.currentTarget.value)}
                            onBlur={validateForm}
                            maxLength={40}
                          />
                          {
                            townValidates === false &&
                            <div className={classNames(formStyles.error, formStyles.errorInDoubleBlock)}>Please enter your town</div>
                          }
                          {
                            (townValidates !== false && postcodeValidates === false) && <div />
                          }

                          <input
                            className={formStyles.input}
                            type="text"
                            name="postcode"
                            placeholder='Postcode'
                            value={postcode}
                            onChange={(e) => setPostcode(e.currentTarget.value)}
                            onBlur={validateForm}
                            maxLength={9}
                          />
                          {
                            postcodeValidates === false &&
                            <div className={classNames(formStyles.error, formStyles.errorInDoubleBlock)}>Please check your postcode</div>
                          }
                        </div>

                        <div className={formStyles.question}>{data.footer.formQuestion}</div>

                        <div className={classNames(formStyles.doubleBlock, formStyles.doubleBlockSingleSmall)}>
                          <div>
                            <Checkbox
                              id={'theatre-checkbox'}
                              isSelected={theatre}
                              toggleSelected={toggleTheatre}>
                              Theatre &amp; Dance
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'art-checkbox'}
                              isSelected={art}
                              toggleSelected={toggleArt}>
                              Art &amp; Design
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'community-checkbox'}
                              isSelected={community}
                              toggleSelected={toggleCommunity}>
                              Community &amp; Education
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'contemporary-checkbox'}
                              isSelected={contemporary}
                              toggleSelected={toggleContemporary}>
                              Contemporary Music
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'classical-checkbox'}
                              isSelected={classical}
                              toggleSelected={toggleClassical}>
                              Classical Music
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'comedy-checkbox'}
                              isSelected={comedy}
                              toggleSelected={toggleComedy}>
                              Comedy
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'literature-checkbox'}
                              isSelected={literature}
                              toggleSelected={toggleLiterature}>
                              Literature &amp; Spoken Word
                          </Checkbox>
                          </div>
                          <div>
                            <Checkbox
                              id={'family-checkbox'}
                              isSelected={family}
                              toggleSelected={toggleFamily}>
                              Children &amp; Family
                          </Checkbox>
                          </div>
                        </div>
                        <button type={'submit'} className={classNames('button', formStyles.signUpButton)}>{data.footer.formSubmitButtonText} →</button>
                      </form>
                    }

                  </div>
                  <FormChevrons />
                </div>
              </div>
            }
          </div>
          , document.body)
      }
    </>
  )
}

export default SignUpForm;